import { LIMITS } from '@athena/shared'

import { ACCEPTED_IMAGE_MIME_TYPES } from '@athena/ui'

/**
 * Which files may be read for a practice, and how many at once.
 *
 * Pure, and the only place these rules live. They are the whole of the
 * validation for this flow, because nothing crosses a server boundary: the
 * images are read by Tesseract in the tab that chose them and are never sent
 * anywhere (see `ocr.ts`). There is therefore no endpoint to validate at, and
 * adding one to re-check what only the browser ever sees would be an upload
 * API with no upload.
 *
 * What these rules protect against is the browser itself. An arbitrary file
 * handed to a wasm decoder is untrusted input, so a file gets through here
 * only if it declares one of three image types, is small enough to decode
 * without exhausting the tab, and — see `decodesAsImage` — actually decodes as
 * a picture. Nothing ever executes, interprets, or stores what is in it.
 */

/** The three the design system's picker offers, and the three Tesseract reads. */
export const ACCEPTED_TYPES: readonly string[] = ACCEPTED_IMAGE_MIME_TYPES

export const MAX_IMAGE_BYTES = LIMITS.MAX_IMAGE_BYTES
export const MAX_IMAGE_MEGABYTES = MAX_IMAGE_BYTES / (1024 * 1024)

/**
 * Roughly a chapter's worth of worksheet pages.
 *
 * From `@athena/shared` rather than declared here, because the API bounds the
 * same number on the way in: the browser is untrusted, and two copies of this
 * would be two answers to "how many pages is too many".
 */
export const MAX_IMAGES = LIMITS.MAX_EXTRACTION_IMAGES

export type RejectionReason = 'type' | 'size' | 'count' | 'unreadable' | 'duplicate'

export interface RejectedFile {
  name: string
  reason: RejectionReason
}

export interface ScreenedFiles {
  /** In the order they were given. Order is what makes page 1 read before page 2. */
  accepted: readonly File[]
  rejected: readonly RejectedFile[]
}

/**
 * The type check that does not trust the extension.
 *
 * `File.type` is the browser's sniff of the file, not a read of its name, so
 * `worksheet.png` holding a PDF arrives as `application/pdf` and is turned
 * away here. A file the browser cannot type at all arrives as `''`, which is
 * also not one of the three.
 */
function hasAcceptedType(file: File): boolean {
  return ACCEPTED_TYPES.includes(file.type)
}

/**
 * Sort a batch of chosen files into the ones that may be read and the ones
 * that may not, given what is already selected.
 *
 * Rejections are reported rather than dropped, so the tutor is told which file
 * was refused and why. The count limit applies across the whole selection, not
 * per batch, which is why the names already chosen are a parameter — and they
 * are the names rather than a count because of the duplicate rule below.
 *
 * ── Duplicates, by name ──────────────────────────────────────────────────
 *
 * A tutor who drops the same folder twice, or picks page 3 again because they
 * lost track, should get the selection they meant rather than two of
 * everything. A second `question-01.png` is refused here and told about: the
 * one already in the list is left exactly as it is, still carrying whatever
 * reading state it had.
 *
 * The name is the whole test. Not a hash of the bytes: two genuinely different
 * pages that happen to share a name is a case nobody has, and a tutor who
 * renames a file has said it is a different page. It is also the only test
 * that can be made before the file is decoded, which is the point — this runs
 * ahead of `decodesAsImage`, so a duplicate costs no decode, no compression
 * and no request.
 *
 * Within one batch too, so dropping the same file twice in a single gesture
 * adds it once.
 */
export function screenImageFiles(
  files: readonly File[],
  alreadySelected: readonly string[],
): ScreenedFiles {
  const accepted: File[] = []
  const rejected: RejectedFile[] = []
  const names = new Set(alreadySelected)

  for (const file of files) {
    if (names.has(file.name)) {
      rejected.push({ name: file.name, reason: 'duplicate' })
      continue
    }
    if (!hasAcceptedType(file)) {
      rejected.push({ name: file.name, reason: 'type' })
      continue
    }
    if (file.size > MAX_IMAGE_BYTES) {
      rejected.push({ name: file.name, reason: 'size' })
      continue
    }
    if (alreadySelected.length + accepted.length >= MAX_IMAGES) {
      rejected.push({ name: file.name, reason: 'count' })
      continue
    }
    accepted.push(file)
    names.add(file.name)
  }

  return { accepted, rejected }
}

/**
 * Whether the bytes really are a picture, not just labelled as one.
 *
 * The declared type is a claim by the browser about the first few bytes;
 * decoding is the thing that settles it, and a file that fails here never
 * reaches Tesseract. `createImageBitmap` decodes off the main thread and is
 * closed immediately, so nothing is kept but the answer.
 *
 * Absent in jsdom, and only in jsdom — every browser this app supports has
 * had it for years. Where it is missing the answer is "cannot tell", and this
 * says so by accepting: the file has already passed the type and size checks,
 * and refusing every image in a test environment would make the flow
 * untestable while protecting nobody.
 */
export async function decodesAsImage(file: File): Promise<boolean> {
  if (typeof createImageBitmap !== 'function') return true

  try {
    const bitmap = await createImageBitmap(file)
    bitmap.close()
    return true
  } catch {
    return false
  }
}
